export interface ProcessingProgress {
  current: number;
  total: number;
  phase: 'reading' | 'converting' | 'wasm-converting' | 'processing' | 'complete';
}

export interface ProcessingOptions {
  maxWidth?: number;
  quality?: number;
  maxFileSize?: number;
}

// Helper function to convert blob to base64
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    console.log('📖 FileReader作成...');
    const reader = new FileReader();
    
    reader.onload = () => {
      console.log('✅ FileReader読み込み完了');
      const result = reader.result as string;
      console.log(`📏 Base64データサイズ: ${result.length}文字`);
      resolve(result);
    };
    
    reader.onerror = (error) => {
      console.error('❌ FileReader読み込み失敗:', error);
      console.error('🔍 FileReaderエラー詳細:', {
        error: reader.error,
        readyState: reader.readyState
      });
      reject(new Error(`FileReader failed: ${reader.error?.message || 'Unknown error'}`));
    };
    
    console.log('🚀 FileReader.readAsDataURL開始...');
    reader.readAsDataURL(blob);
  });
}

// Optimized thumbnail creation using createImageBitmap
export async function createThumbnail(file: File): Promise<string> {
  try {
    console.log(`🖼️ サムネイル作成開始: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    console.log('📋 ファイル情報:', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified
    });
    
    // Use createImageBitmap for more efficient memory usage
    console.log('🚀 createImageBitmap開始...');
    const imageBitmap = await createImageBitmap(file);
    console.log(`📐 画像サイズ取得: ${imageBitmap.width}x${imageBitmap.height}px`);
    console.log('✅ ImageBitmap作成成功');
    
    // Calculate thumbnail dimensions
    const maxWidth = 300;
    const maxHeight = 300;
    let width = imageBitmap.width;
    let height = imageBitmap.height;

    if (width > height) {
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
    } else {
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }
    }

    console.log(`📏 サムネイルサイズ決定: ${width}x${height}px`);
    
    // Create thumbnail
    console.log('🎨 Canvas要素作成開始...');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    console.log(`📏 Canvas設定完了: ${canvas.width}x${canvas.height}px`);
    
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error('❌ Canvas取得失敗: Canvas contextを取得できません');
      imageBitmap.close(); // Clean up ImageBitmap
      throw new Error('Failed to get canvas context');
    }

    // Use better quality settings
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    console.log('🎨 描画設定: 高品質アンチエイリアシングを有効化');
    
    // Draw and compress
    ctx.drawImage(imageBitmap, 0, 0, width, height);
    console.log('✏️ Canvas描画: 画像をCanvasに描画完了');
    
    // Clean up ImageBitmap immediately
    imageBitmap.close();
    console.log('🧹 メモリ解放: ImageBitmapをクリーンアップ');
    
    // Convert to blob first, then to base64 for memory efficiency
    return new Promise((resolve, reject) => {
      console.log('🔄 変換処理: CanvasをJPEGに変換中...');
      console.log('⚙️ toBlob設定: JPEG品質0.2');
      
      try {
      canvas.toBlob((blob) => {
          console.log('📞 toBlobコールバック呼び出し');
        if (!blob) {
            console.error('❌ Blob変換失敗: サムネイルBlobの作成に失敗');
            console.error('🔍 Canvas状態:', {
              width: canvas.width,
              height: canvas.height,
              hasContext: !!ctx
            });
          reject(new Error('Failed to create thumbnail blob'));
          return;
        }
          console.log(`💾 Blob作成完了: ${(blob.size / 1024).toFixed(2)}KB`);
          console.log('📝 Blob詳細:', {
            size: blob.size,
            type: blob.type
          });
          
          console.log('🔄 Base64変換開始...');
          blobToBase64(blob)
            .then((base64) => {
              console.log(`✅ Base64変換完了: ${base64.length}文字`);
              resolve(base64);
            })
            .catch((error) => {
              console.error('❌ Base64変換失敗:', error);
              reject(error);
            });
      }, 'image/jpeg', 0.2);
      } catch (toBlobError) {
        console.error('❌ toBlob呼び出し失敗:', toBlobError);
        reject(new Error(`toBlob failed: ${toBlobError instanceof Error ? toBlobError.message : 'Unknown error'}`));
      }
    });
  } catch (error) {
    console.error('❌ サムネイル作成失敗:', error);
    console.error('🔍 エラー詳細:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    throw new Error(`Failed to create thumbnail: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Optimized image preview creation
export async function createImagePreview(file: File): Promise<{ original: string; thumbnail: string }> {
  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    console.error(`❌ ファイルサイズ超過: ${file.name} は10MBを超えています`);
    throw new Error('File size exceeds 10MB limit');
  }

  try {
    console.log(`🔄 画像プレビュー作成開始: ${file.name}`);
    
    // Process thumbnail and original in parallel for better performance
    const [original, thumbnail] = await Promise.all([
      blobToBase64(file),
      createThumbnail(file)
    ]);
    
    console.log(`✅ 画像プレビュー作成完了: ${file.name}`);
    return { original, thumbnail };
  } catch (error) {
    console.error(`❌ 画像プレビュー作成失敗: ${file.name}`, error);
    throw new Error(`Failed to create image preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export interface ProcessImageBatchOptions {
  /** When true, sort by filename (e.g. desktop drag-and-drop). Default: preserve input order. */
  sortByFilename?: boolean;
}

// Process images in smaller batches to prevent memory issues
export async function processImageBatch(
  files: File[],
  onProgress?: (progress: ProcessingProgress) => void,
  options: ProcessImageBatchOptions = {}
): Promise<Array<{ original: string; thumbnail: string } | null>> {
  const boundedFiles = files.slice(0, 5000);
  const filesToProcess = options.sortByFilename
    ? boundedFiles
        .filter(file => file.type.startsWith('image/'))
        .sort((a, b) => a.name.localeCompare(b.name))
    : boundedFiles;
  
  console.log('🎯 processImageBatch開始:', {
    '入力ファイル数': files.length,
    '処理対象数': filesToProcess.length,
    'sortByFilename': options.sortByFilename ?? false
  });
  
  const results: Array<{ original: string; thumbnail: string } | null> =
    new Array(filesToProcess.length).fill(null);
  const batchSize = 5; // Process 5 images at a time to prevent memory spikes
  
  console.log(`⚡ バッチサイズ: ${batchSize}個ずつ並列処理を実行`);
  
  for (let i = 0; i < filesToProcess.length; i += batchSize) {
    const batch = filesToProcess.slice(i, i + batchSize);
    console.log(`📦 バッチ ${Math.floor(i/batchSize) + 1}/${Math.ceil(filesToProcess.length/batchSize)}: ${batch.length}個のファイルを処理中`);
    
    // Process batch in parallel
    const batchPromises = batch.map(async (file, batchIndex) => {
      const globalIndex = i + batchIndex;
      
      if (onProgress) {
        onProgress({
          current: globalIndex,
          total: filesToProcess.length,
          phase: 'processing'
        });
      }

      if (!file.type.startsWith('image/')) {
        console.warn(`⚠️ 画像${globalIndex + 1}スキップ: ${file.name} (非画像MIME)`);
        return { result: null, index: globalIndex };
      }

      try {
        console.log(`🎨 画像${globalIndex + 1}/${filesToProcess.length}: ${file.name} の処理開始`);
        const result = await createImagePreview(file);
        console.log(`✅ 画像${globalIndex + 1}完了: ${file.name}`);
        return { result, index: globalIndex };
      } catch (error) {
        console.error(`❌ 画像${globalIndex + 1}失敗: ${file.name}`, error);
        return { result: null, index: globalIndex };
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    console.log(`✅ バッチ ${Math.floor(i/batchSize) + 1} 完了`);
    
    for (const item of batchResults) {
      results[item.index] = item.result;
    }
    
    // Force garbage collection between batches by clearing references
    if (i + batchSize < filesToProcess.length) {
      await new Promise(resolve => setTimeout(resolve, 10)); // Small delay to allow GC
    }
  }

  // Final progress update
  if (onProgress) {
    onProgress({
      current: filesToProcess.length,
      total: filesToProcess.length,
      phase: 'complete'
    });
  }
  
  console.log('🎉 processImageBatch完了:', {
    '成功画像数': results.filter(Boolean).length,
    '総処理数': filesToProcess.length
  });
  
  return results;
}

// Add a utility function to clear image data from memory
export function clearImageData(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }
  canvas.width = 0;
  canvas.height = 0;
}

// Add a function to process a single image file efficiently
export async function processSingleImage(file: File): Promise<{ original: string; thumbnail: string }> {
  try {
    return await createImagePreview(file);
  } catch (error) {
    console.error(`Failed to process single image: ${file.name}`, error);
    throw error;
  }
}
　
// Utility function to estimate memory usage
export function estimateImageMemoryUsage(files: File[]): number {
  return files.reduce((total, file) => total + file.size, 0) * 2; // Estimate 2x file size for processing
}

// Check if device has sufficient memory for processing
export function checkMemoryAvailability(): boolean {
  // Check if we're on a mobile device with limited memory
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // For mobile devices, be more conservative
    return (performance as any).memory ? (performance as any).memory.usedJSHeapSize < 50 * 1024 * 1024 : true; // 50MB threshold
  }
  
  // For desktop, allow more memory usage
  return (performance as any).memory ? (performance as any).memory.usedJSHeapSize < 200 * 1024 * 1024 : true; // 200MB threshold
}